import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../Logo/Logo";
import ThemeToggle from "../ThemeToggle/ThemeToggle";
import { fetchCategories } from "../../api/newsApi";
import { useAuth } from "../../context/AuthContext";
import "./TopBar.css";

function TopBar() {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openCategoryId, setOpenCategoryId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const topBarRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const todayLabel = new Date().toLocaleDateString("en-US", {
    weekday: undefined,
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  useEffect(() => {
    let isMounted = true;

    async function loadCategories() {
      try {
        const data = await fetchCategories();
        if (isMounted) setCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        if (isMounted) setCategories([]);
      } finally {
        if (isMounted) setLoadingCategories(false);
      }
    }

    loadCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  // Close the menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (topBarRef.current && !topBarRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close the menu on Escape
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // Lock body scroll while the overlay is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  function closeMenu() {
    setMenuOpen(false);
    setOpenCategoryId(null);
  }

  function toggleCategory(categoryId) {
    setOpenCategoryId((prev) => (prev === categoryId ? null : categoryId));
  }

  function handleSearchSubmit(event) {
    event.preventDefault();
    const term = searchTerm.trim();
    if (term) {
      navigate(`/search?q=${encodeURIComponent(term)}`);
      setSearchTerm("");
      closeMenu();
    }
  }

  function handleLogout() {
    logout();
    closeMenu();
  }

  return (
    <header className="tt-topbar-wrap" ref={topBarRef}>
      <div className="tt-topbar-inner">
        {/* <button
          type="button"
          className="tt-topbar-hamburger"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          <span className={`tt-topbar-hamburger-bar ${menuOpen ? "tt-topbar-hamburger-open" : ""}`} />
        </button> */}
        <div className= "tt-topbar-search-slot">
          <Link to="/search" className="tt-topbar-search-btn" aria-label="Search">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="tt-topbar-search-btn-label">Search</span>
          </Link>
        </div>

        <div className="tt-topbar-logo-slot">
          <Logo size="small" />
        </div>

        <div className="tt-topbar-actions">
          <ThemeToggle />

          {user ? (
            <div className="tt-topbar-user-actions">
              <Link to="/profile" className="tt-topbar-user">Hi, {user.name}</Link>
              <button type="button" className="tt-topbar-auth-btn" onClick={handleLogout}>
                Logout
              </button>
            </div>
          ) : (
            <div className="tt-topbar-user-actions">
              <Link to="/login" className="tt-topbar-auth-btn tt-topbar-auth-btn-outline">
                Login
              </Link>
              <Link to="/register" className="tt-topbar-auth-btn">
                Register
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Secondary bar: today's date, quick category links, and a button
          to the dedicated search page. The row above (logo/hamburger/auth)
          is untouched. */}
      <div className="tt-topbar-subbar">
        <div className="tt-topbar-subbar-inner">
          <span className="tt-topbar-date">{todayLabel}</span>

          <nav className="tt-topbar-catnav" aria-label="Categories">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="tt-topbar-catnav-link"
              >
                {category.name}
              </Link>
            ))}
          </nav>

          {/* <Link to="/search" className="tt-topbar-search-btn" aria-label="Search">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="tt-topbar-search-btn-label">Search</span>
          </Link> */}
        </div>
      </div>

      {/* Full category mega-menu, opened by the hamburger. Clicking the
          transparent backdrop (outside the panel itself) closes it. */}
      <div
        className={`tt-topbar-overlay ${menuOpen ? "tt-topbar-overlay-open" : ""}`}
        onClick={(event) => {
          if (event.target === event.currentTarget) closeMenu();
        }}
      >
        <div className="tt-topbar-overlay-inner">
          <form className="tt-topbar-search-form" onSubmit={handleSearchSubmit}>
            <svg className="tt-topbar-search-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              className="tt-topbar-search-input"
              placeholder="Search Today Talks"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </form>

          {loadingCategories && <p className="tt-topbar-loading">Loading categories...</p>}
          {!loadingCategories && categories.length === 0 && (
            <p className="tt-topbar-loading">No categories available</p>
          )}

          <div className="tt-topbar-cat-grid">
            {categories.map((category) => {
              const hasSubcategories = category.subcategories && category.subcategories.length > 0;
              const isActive = openCategoryId === category.id;

              return (
                <React.Fragment key={category.id}>
                  {hasSubcategories ? (
                    <button
                      type="button"
                      className={`tt-topbar-cat-item ${isActive ? "tt-topbar-cat-item-active" : ""}`}
                      onClick={() => toggleCategory(category.id)}
                      aria-expanded={isActive}
                    >
                      <span>{category.name}</span>
                      <svg
                        className={`tt-topbar-cat-caret ${isActive ? "tt-topbar-cat-caret-open" : ""}`}
                        viewBox="0 0 20 20"
                        width="13"
                        height="13"
                        aria-hidden="true"
                      >
                        <path
                          d="M5 7l5 5 5-5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  ) : (
                    <Link
                      to={`/category/${category.slug}`}
                      className="tt-topbar-cat-item"
                      onClick={closeMenu}
                    >
                      <span>{category.name}</span>
                    </Link>
                  )}

                  {hasSubcategories && isActive && (
                    <div className="tt-topbar-cat-panel">
                      <span className="tt-topbar-cat-panel-label">Section</span>
                      <div className="tt-topbar-cat-panel-list">
                        {category.subcategories.map((sub) => (
                          <Link
                            key={sub.id}
                            to={`/category/${sub.slug}`}
                            className="tt-topbar-cat-panel-link"
                            onClick={closeMenu}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </header>
  );
}

export default TopBar;
